/**
 * 键盘交互工具。
 */
import { useRef, useState } from 'react';
import type { KeyboardEventHandler } from 'react';

/** [useEnterToSearch] 返回值:整个摊到 Select 上。 */
interface EnterToSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSearch: (value: string) => void;
  onSelect: () => void;
  onBlur: () => void;
  onInputKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

/**
 * 让筛选栏里的 antd Select「回车即查询,且不弹下拉」;展开改用方向键 ↓ 或鼠标点。
 *
 * Enter 在 Select 上本来被占了三份用途,这里按当前状态分流,只在都不适用时才查询:
 *   - tags 框有未提交的输入 → 让位给「把输入转成标签」
 *   - 下拉已展开           → 让位给 OptionList 选中高亮项
 *   - 其余                → 查询
 *
 * ## 为什么必须受控 open
 *
 * rc-select 有两条各自独立的「Enter 就展开下拉」路径,都堵不住:
 *   1. Selector 的 `isValidateOpenKey(which)` → `onToggleOpen(true)`。它紧跟在调用
 *      onInputKeyDown 之后、同一函数体内,冒泡拦不住;`which` 在函数开头已取快照,
 *      在 handler 里改 event 也影响不到;rc-select 又不看 defaultPrevented。
 *   2. 冒泡到容器 div 后 BaseSelect 的 keydown「见 Enter 且下拉关着就展开」。
 *
 * 两条最终都汇到 `onToggleOpen`——它在受控 open 下改不动实际状态,只会回调 onOpenChange。
 * 于是不去堵路径,改为在这个出口否决:handler 判定这次 Enter 归查询后置位标记,
 * onOpenChange 见标记就丢弃这次展开请求。
 *
 * 注意一次 Enter 会**两条路径各请求一次**展开,所以标记不能读一次就清,否则第二次漏过去;
 * 两次都在同一轮同步派发内到达,故用微任务清除。标记走 ref 不走 state——同一次按键内
 * 同步读写,等不到重渲染。
 *
 * ## 顺带解决 tags 框
 *
 * tags 框的下拉在空输入时列的是**已选中的标签**,留着开会让 Enter 命中 OptionList 把
 * 高亮那条取消选中——等于回车删掉自己刚输的标签。这里靠 onSearch 只在有输入时展开
 * (保留「点此添加」提示),空输入即收起,Enter 便安全落到查询上。
 *
 * 收起要三个信号一起收,少一个就会漏成「下拉常开」:
 *   - onSearch('')  清空输入框
 *   - onSelect      回车转标签。这条最容易漏——rc-select 转标签走 onInternalSearch 的
 *                   `source === 'submit'` 分支,内部 setSearchValue('') 后直接 return,
 *                   **不会**回调 onSearch,所以只听 onSearch 收不到这次清空。
 *   - onBlur        失焦
 *
 * 用法:顶层 `const enterToSearch = useEnterToSearch(handleSearch);`,
 * 每个框写 `<Select {...enterToSearch(1)} />`,序号在同一筛选栏内不重复即可
 * (只用来区分各框的下拉开合状态,不参与业务)。
 * 别在同一个 Select 上另写 open / onOpenChange / onSearch / onSelect / onBlur /
 * onInputKeyDown,会覆盖掉这里的。
 */
export function useEnterToSearch(onSearch: () => void): (key: number) => EnterToSearchProps {
  // 同一筛选栏同时最多开一个下拉,所以只记「当前展开的是哪个框」,
  // 不必给每个框各开一份 useState。
  const [openKey, setOpenKey] = useState<number | null>(null);
  // 「这次 Enter 已判给查询」——置位后否决随之而来的展开请求。
  const searchedRef = useRef(false);

  return (key) => {
    const open = openKey === key;
    const close = () => setOpenKey((cur) => (cur === key ? null : cur));
    return {
      open,
      onOpenChange: (next) => {
        if (next && searchedRef.current) return;
        if (next) setOpenKey(key);
        else close();
      },
      onSearch: (value) => (value ? setOpenKey(key) : close()),
      onSelect: close,
      onBlur: close,
      onInputKeyDown: (e) => {
        if (e.key !== 'Enter') return;

        // 中文输入法组字期间的 Enter 是「上屏」不是「确认查询」。isComposing 走原生事件
        // (React 合成事件上没有);keyCode 229 是 Safari 兜底——它的 compositionend 可能
        // 早于结束合成的这次 keydown,那时 isComposing 已翻 false。
        if (e.nativeEvent.isComposing || e.keyCode === 229) return;

        // 还有没转成标签的输入 → 这次 Enter 归转标签
        if ((e.target as HTMLInputElement).value.trim()) return;

        // 下拉展开时 Enter 归 OptionList(选中/取消选中高亮项),不能抢
        if (open) return;

        // 置位否决这次 Enter 引发的展开请求(两条路径各来一次,都在本轮同步派发完),
        // 再用微任务清掉。不能留到下次 keydown 才清:用户按完 Enter 直接用鼠标点
        // 下拉,走的是 onMouseDown 而非 keydown,标记不清就会把鼠标也一起挡住。
        searchedRef.current = true;
        queueMicrotask(() => { searchedRef.current = false; });
        onSearch();
      },
    };
  };
}
